/**
 * Armadillo Adamantium - Client SDK
 * Phoenix Architecture: Browser-side security utilities
 * 
 * This SDK provides client-side functions for:
 * - Browser fingerprinting
 * - Entropy nonce generation
 * - Gödel challenge solving
 * - Proof-of-Work solving
 * - Capability token management
 * 
 * Usage: Import in your React components or API calls
 */

import { sha256 } from 'js-sha256';

// ============================================
// BROWSER FINGERPRINTING
// ============================================

/**
 * Generate comprehensive browser fingerprint
 * Combines multiple browser signals for unique identification
 */
export async function generateFingerprint(): Promise<string> {
  const signals = [
    navigator.userAgent,
    navigator.language,
    navigator.platform,
    navigator.hardwareConcurrency?.toString() || 'unknown',
    screen.width + 'x' + screen.height,
    screen.colorDepth,
    new Date().getTimezoneOffset().toString(),
    !!window.localStorage,
    !!window.indexedDB,
    !!window.WebGLRenderingContext
  ];
  
  // Add canvas fingerprint (if available)
  try {
    const canvas = document.createElement('canvas');
    const ctx = canvas.getContext('2d');
    if (ctx) {
      canvas.width = 200;
      canvas.height = 50;
      ctx.textBaseline = 'top';
      ctx.font = '14px Arial';
      ctx.fillText('Armadillo Fingerprint', 2, 2);
      signals.push(canvas.toDataURL());
    }
  } catch {
    // Canvas blocked
  }
  
  // Add WebGL fingerprint
  try {
    const canvas = document.createElement('canvas');
    const gl = canvas.getContext('webgl');
    if (gl) {
      const debugInfo = gl.getExtension('WEBGL_debug_renderer_info');
      if (debugInfo) {
        signals.push(gl.getParameter(debugInfo.UNMASKED_RENDERER_WEBGL));
        signals.push(gl.getParameter(debugInfo.UNMASKED_VENDOR_WEBGL));
      }
    }
  } catch {
    // WebGL blocked
  }
  
  // Hash all signals
  const raw = signals.join('|');
  return await sha256(raw);
}

// ============================================
// ENTROPY NONCE GENERATION
// ============================================

/**
 * Generate entropy nonce with timing jitter
 * Used to prove human presence (bots have predictable timing)
 */
export function generateEntropyNonce(): string {
  const timestamp = Date.now();
  
  // Add human timing jitter (random delay 0-100ms)
  const jitter = Math.floor(Math.random() * 100);
  
  // Counter for request ordering
  const counter = parseInt(localStorage.getItem('armadillo_nonce_counter') || '0');
  localStorage.setItem('armadillo_nonce_counter', (counter + 1).toString());
  
  // Format: "timestamp:jitter:counter"
  return `${timestamp}:${jitter}:${counter}`;
}

/**
 * Simulate human-like delay before request
 * Adds natural timing variance
 */
export async function humanDelay(minMs: number = 50, maxMs: number = 200): Promise<void> {
  const delay = minMs + Math.random() * (maxMs - minMs);
  return new Promise(resolve => setTimeout(resolve, delay));
}

// ============================================
// GÖDEL CHALLENGE SOLVER
// ============================================

/**
 * Solve Gödel challenge (Wang tile puzzle)
 * Returns colored grid satisfying adjacency constraints
 */
export async function solveGodelChallenge(
  challenge: string,
  tileSet: string
): Promise<string | null> {
  const startTime = Date.now();
  
  try {
    // Parse challenge
    const challengeData = JSON.parse(challenge);
    const { gridSize, initialColors } = challengeData;
    
    // Parse tile set
    const colors = JSON.parse(atob(tileSet));
    
    // Build solution grid
    const solution: number[][] = [];
    
    // Initialize with initial colors
    for (let i = 0; i < gridSize; i++) {
      solution[i] = [];
      for (let j = 0; j < gridSize; j++) {
        const key = `${i},${j}`;
        solution[i][j] = initialColors[key] !== undefined ? initialColors[key] : -1;
      }
    }
    
    // Solve using backtracking (simulated human approach)
    const solved = solveGrid(solution, 0, 0, gridSize);
    
    if (!solved) {
      return null;
    }
    
    // Add human-like delay (1-10 seconds)
    const elapsed = Date.now() - startTime;
    const minHumanTime = 1000;
    if (elapsed < minHumanTime) {
      await new Promise(resolve => setTimeout(resolve, minHumanTime - elapsed));
    }
    
    return JSON.stringify(solution);
  } catch (error) {

    return null;
  }
}

/**
 * Backtracking solver for Wang tile grid
 */
function solveGrid(
  grid: number[][],
  row: number,
  col: number,
  gridSize: number
): boolean {
  // Base case: all cells filled
  if (row === gridSize) {
    return true;
  }
  
  // Move to next row
  if (col === gridSize) {
    return solveGrid(grid, row + 1, 0, gridSize);
  }
  
  // Skip already filled cells
  if (grid[row][col] !== -1) {
    return solveGrid(grid, row, col + 1, gridSize);
  }
  
  // Try each color (0-3)
  for (let color = 0; color < 4; color++) {
    if (isValidPlacement(grid, row, col, color, gridSize)) {
      grid[row][col] = color;
      
      if (solveGrid(grid, row, col + 1, gridSize)) {
        return true;
      }
      
      grid[row][col] = -1; // Backtrack
    }
  }
  
  return false;
}

/**
 * Check if color placement is valid
 */
function isValidPlacement(
  grid: number[][],
  row: number,
  col: number,
  color: number,
  gridSize: number
): boolean {
  // Check left neighbor
  if (col > 0 && grid[row][col - 1] === color) {
    return false;
  }
  
  // Check top neighbor
  if (row > 0 && grid[row - 1][col] === color) {
    return false;
  }
  
  return true;
}

// ============================================
// PROOF-OF-WORK SOLVER
// ============================================

/**
 * Solve proof-of-work challenge
 * Brute-force search for nonce with leading zeros
 */
export async function solvePoWChallenge(
  challenge: string,
  difficulty: number,
  maxAttempts: number = 100000
): Promise<string | null> {
  const startTime = Date.now();
  
  for (let nonce = 0; nonce < maxAttempts; nonce++) {
    const hash = await sha256(`${challenge}:${nonce}`);
    const binaryHash = parseInt(hash.substring(0, 8), 16).toString(2).padStart(32, '0');
    const leadingZeros = binaryHash.match(/^0+/)?.[0].length || 0;
    
    if (leadingZeros >= difficulty) {

      return nonce.toString();
    }
  }
  
  return null;
}

// ============================================
// CAPABILITY TOKEN MANAGEMENT
// ============================================

/**
 * Get stored capability token
 */
export function getCapabilityToken(): string | null {
  return localStorage.getItem('armadillo_capability_token');
}

/**
 * Store capability token
 */
export function storeCapabilityToken(token: string): void {
  localStorage.setItem('armadillo_capability_token', token);
}

/**
 * Clear capability token (logout)
 */
export function clearCapabilityToken(): void {
  localStorage.removeItem('armadillo_capability_token');
  localStorage.removeItem('armadillo_trust_score');
}

/**
 * Add capability token to fetch request
 */
export function withCapabilityToken(headers: HeadersInit = {}): HeadersInit {
  const token = getCapabilityToken();
  
  if (token) {
    return {
      ...headers,
      'Authorization': `Bearer ${token}`,
      'X-Entropy-Nonce': generateEntropyNonce()
    };
  }
  
  return {
    ...headers,
    'X-Entropy-Nonce': generateEntropyNonce()
  };
}

// ============================================
// SECURE FETCH WRAPPER
// ============================================

/**
 * Fetch wrapper with automatic Armadillo security
 */
export async function armadilloFetch(
  url: string,
  options: RequestInit = {}
): Promise<Response> {
  // Add security headers
  const headers = withCapabilityToken(options.headers as HeadersInit);
  
  // Add entropy nonce
  headers['X-Entropy-Nonce'] = generateEntropyNonce();
  headers['X-Fingerprint'] = await generateFingerprint();
  
  // Merge with existing headers
  const mergedHeaders = {
    ...options.headers,
    ...headers
  };
  
  // Make request
  const response = await fetch(url, {
    ...options,
    headers: mergedHeaders
  });
  
  // Handle capability token in response
  const newToken = response.headers.get('X-Capability-Token');
  if (newToken) {
    storeCapabilityToken(newToken);
  }
  
  // Handle Gödel challenge
  if (response.status === 403) {
    const challengeType = response.headers.get('X-Challenge-Type');
    
    if (challengeType === 'godel') {
      const data = await response.json();
      const solution = await solveGodelChallenge(data.challenge, data.tileSet);
      
      if (solution) {
        // Retry with solution
        headers['X-Godel-Solution'] = solution;
        
        return fetch(url, {
          ...options,
          headers: mergedHeaders
        });
      }
    }
  }
  
  return response;
}

// ============================================
// SESSION MANAGEMENT
// ============================================

/**
 * Initialize Armadillo session
 * Call this on app startup
 */
export async function initializeSession(): Promise<void> {
  // Generate and store fingerprint
  const fingerprint = await generateFingerprint();
  localStorage.setItem('armadillo_fingerprint', fingerprint);
  
  // Reset nonce counter
  localStorage.setItem('armadillo_nonce_counter', '0');
  

}

/**
 * Clear all Armadillo data (logout + cleanup)
 */
export function clearSession(): void {
  const keysToRemove = Object.keys(localStorage).filter(
    key => key.startsWith('armadillo_')
  );
  
  keysToRemove.forEach(key => localStorage.removeItem(key));
  

}

// ============================================
// HEALTH CHECK
// ============================================

/**
 * Check Armadillo security system health
 */
export async function healthCheck(): Promise<{
  healthy: boolean;
  layers: Record<string, boolean>;
}> {
  const layers: Record<string, boolean> = {};
  
  try {
    // Check fingerprinting
    const fp = await generateFingerprint();
    layers.fingerprint = !!fp;
  } catch {
    layers.fingerprint = false;
  }
  
  try {
    // Check entropy nonce
    const nonce = generateEntropyNonce();
    layers.entropy = !!nonce;
  } catch {
    layers.entropy = false;
  }
  
  try {
    // Check localStorage
    localStorage.setItem('armadillo_health', 'ok');
    layers.storage = localStorage.getItem('armadillo_health') === 'ok';
  } catch {
    layers.storage = false;
  }
  
  const healthy = Object.values(layers).every(v => v);
  
  return { healthy, layers };
}

// ============================================
// POLISH: RESUMABLE STATE, OFFLINE FALLBACK, ANTI-TAMPER
// ============================================

import { ChallengeState, ChallengeModality } from './types';

const challengeStates = new Map<string, ChallengeState>();
const TAMPER_CHECK_KEY = '__armadillo_integrity';

/**
 * Save challenge state for later resumption
 * Allows users to close/reopen browser without losing challenge progress
 */
export function saveChallengeState(state: ChallengeState): void {
  challengeStates.set(state.challengeId, state);
  try {
    sessionStorage.setItem(`armadillo_challenge:${state.challengeId}`, JSON.stringify(state));
  } catch { /* storage unavailable */ }
}

/**
 * Resume a previously saved challenge
 */
export function resumeChallengeState(challengeId: string): ChallengeState | null {
  // Check in-memory first
  const memState = challengeStates.get(challengeId);
  if (memState && Date.now() < memState.expiresAt) return memState;
  
  // Fall back to session storage
  try {
    const stored = sessionStorage.getItem(`armadillo_challenge:${challengeId}`);
    if (stored) {
      const state: ChallengeState = JSON.parse(stored);
      if (Date.now() < state.expiresAt) {
        challengeStates.set(challengeId, state);
        return state;
      }
    }
  } catch { /* storage unavailable */ }
  
  return null;
}

/**
 * Offline-safe fallback: generate a local proof that can be verified later
 */
export function generateOfflineProof(challengeId: string, solution: string): string {
  const timestamp = Date.now();
  const proof = `${challengeId}:${timestamp}:${btoa(solution)}`;
  return proof;
}

/**
 * Verify an offline proof
 */
export function verifyOfflineProof(proof: string): { valid: boolean; age: number } {
  try {
    const parts = proof.split(':');
    if (parts.length < 3) return { valid: false, age: 0 };
    const timestamp = parseInt(parts[1]);
    const age = Date.now() - timestamp;
    // Valid for 24 hours
    return { valid: age < 86400000, age };
  } catch {
    return { valid: false, age: 0 };
  }
}

/**
 * Anti-tamper check: detect if security-critical code has been modified
 */
export function initializeTamperCheck(): void {
  try {
    const integrityHash = generateFingerprint.toString().slice(0, 100);
    localStorage.setItem(TAMPER_CHECK_KEY, btoa(integrityHash));
  } catch { /* storage unavailable */ }
}

/**
 * Verify tamper check
 */
export function verifyTamperCheck(): boolean {
  try {
    const stored = localStorage.getItem(TAMPER_CHECK_KEY);
    if (!stored) return true; // No baseline = assume ok
    const expected = atob(stored);
    const current = generateFingerprint.toString().slice(0, 100);
    return expected === current;
  } catch {
    return true; // Fail-open in non-browser environments
  }
}

/**
 * Cleanup expired challenge states
 */
export function cleanupChallengeStates(): number {
  const now = Date.now();
  let cleaned = 0;
  for (const [key, state] of challengeStates) {
    if (now > state.expiresAt) {
      challengeStates.delete(key);
      cleaned++;
    }
  }
  return cleaned;
}
